export type TeacherTitle = 'ms' | 'coach';

export interface TeacherIdentity {
  uid: string;
  displayName?: string | null;
  email?: string | null;
  teacherTitle?: TeacherTitle;
}

export interface TeacherTitleAccount extends TeacherIdentity {
  role?: string | null;
  status?: string | null;
  studentId?: string | null;
}

const LEADING_HONORIFIC = /^(?:ms|miss|mrs|mr|coach|mx)\.?\s+/i;
// Same honorific set as LEADING_HONORIFIC, anchored end to end with no
// trailing word required - catches a display name that IS just a title
// ("Ms.", "Coach", "Mr") with nothing after it. LEADING_HONORIFIC alone
// cannot: it requires \s+ after the optional dot, so "Ms." with nothing
// following never matches it and survives untouched into "first name".
const BARE_HONORIFIC = /^(?:ms|miss|mrs|mr|coach|mx)\.?$/i;

/**
 * ONE CHARACTER THAT CAN NAME SOMEONE: anything outside White_Space,
 * Default_Ignorable_Code_Point, Cc, Cf and M (every mark).
 *
 * SPELLED AS EXPLICIT RANGES, NOT \p{...} PROPERTY ESCAPES. scripts/
 * check-regex-floor.mjs fails the build on a property escape in any built
 * chunk, because an older Safari cannot parse one and a chunk that fails to
 * parse takes every screen that imports it down with it (lastHomeShell.ts and
 * compBoardDances.ts say the same). This class is exactly
 *   /[^\p{White_Space}\p{Default_Ignorable_Code_Point}\p{Cc}\p{Cf}\p{M}]/u
 * compiled by regexpu-core 6.4.0 (Unicode 17.0.0, the tables the build's own
 * @babel/plugin-transform-unicode-property-regex uses) with
 * { unicodePropertyEscapes: 'transform' }. The server mirror
 * (netlify/functions/lib/teacherTitle.mjs) runs on Node, which parses the
 * escapes, so it keeps them. tests/teacher-name-readability.test.mjs sweeps
 * every code point against the escapes on the running Node: this class must
 * reject everything they reject, and may differ only on code points that
 * Node's own Unicode tables have not assigned yet.
 */
const NAME_BASE_CHARACTER = /[^\0- \x7F-\xA0\xAD\u0300-\u036F\u0483-\u0489\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u0600-\u0605\u0610-\u061A\u061C\u064B-\u065F\u0670\u06D6-\u06DD\u06DF-\u06E4\u06E7\u06E8\u06EA-\u06ED\u070F\u0711\u0730-\u074A\u07A6-\u07B0\u07EB-\u07F3\u07FD\u0816-\u0819\u081B-\u0823\u0825-\u0827\u0829-\u082D\u0859-\u085B\u0890\u0891\u0897-\u089F\u08CA-\u0903\u093A-\u093C\u093E-\u094F\u0951-\u0957\u0962\u0963\u0981-\u0983\u09BC\u09BE-\u09C4\u09C7\u09C8\u09CB-\u09CD\u09D7\u09E2\u09E3\u09FE\u0A01-\u0A03\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A70\u0A71\u0A75\u0A81-\u0A83\u0ABC\u0ABE-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AE2\u0AE3\u0AFA-\u0AFF\u0B01-\u0B03\u0B3C\u0B3E-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B55-\u0B57\u0B62\u0B63\u0B82\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD7\u0C00-\u0C04\u0C3C\u0C3E-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C62\u0C63\u0C81-\u0C83\u0CBC\u0CBE-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CE2\u0CE3\u0CF3\u0D00-\u0D03\u0D3B\u0D3C\u0D3E-\u0D44\u0D46-\u0D48\u0D4A-\u0D4D\u0D57\u0D62\u0D63\u0D81-\u0D83\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DF2\u0DF3\u0E31\u0E34-\u0E3A\u0E47-\u0E4E\u0EB1\u0EB4-\u0EBC\u0EC8-\u0ECE\u0F18\u0F19\u0F35\u0F37\u0F39\u0F3E\u0F3F\u0F71-\u0F84\u0F86\u0F87\u0F8D-\u0F97\u0F99-\u0FBC\u0FC6\u102B-\u103E\u1056-\u1059\u105E-\u1060\u1062-\u1064\u1067-\u106D\u1071-\u1074\u1082-\u108D\u108F\u109A-\u109D\u115F\u1160\u135D-\u135F\u1680\u1712-\u1715\u1732-\u1734\u1752\u1753\u1772\u1773\u17B4-\u17D3\u17DD\u180B-\u180F\u1885\u1886\u18A9\u1920-\u192B\u1930-\u193B\u1A17-\u1A1B\u1A55-\u1A5E\u1A60-\u1A7C\u1A7F\u1AB0-\u1ADD\u1AE0-\u1AEB\u1B00-\u1B04\u1B34-\u1B44\u1B6B-\u1B73\u1B80-\u1B82\u1BA1-\u1BAD\u1BE6-\u1BF3\u1C24-\u1C37\u1CD0-\u1CD2\u1CD4-\u1CE8\u1CED\u1CF4\u1CF7-\u1CF9\u1DC0-\u1DFF\u2000-\u200F\u2028-\u202F\u205F-\u206F\u20D0-\u20F0\u2CEF-\u2CF1\u2D7F\u2DE0-\u2DFF\u3000\u302A-\u302F\u3099\u309A\u3164\uA66F-\uA672\uA674-\uA67D\uA69E\uA69F\uA6F0\uA6F1\uA802\uA806\uA80B\uA823-\uA827\uA82C\uA880\uA881\uA8B4-\uA8C5\uA8E0-\uA8F1\uA8FF\uA926-\uA92D\uA947-\uA953\uA980-\uA983\uA9B3-\uA9C0\uA9E5\uAA29-\uAA36\uAA43\uAA4C\uAA4D\uAA7B-\uAA7D\uAAB0\uAAB2-\uAAB4\uAAB7\uAAB8\uAABE\uAABF\uAAC1\uAAEB-\uAAEF\uAAF5\uAAF6\uABE3-\uABEA\uABEC\uABED\uFB1E\uFE00-\uFE0F\uFE20-\uFE2F\uFEFF\uFFA0\uFFF0-\uFFFB\u{101FD}\u{102E0}\u{10376}-\u{1037A}\u{10A01}-\u{10A03}\u{10A05}\u{10A06}\u{10A0C}-\u{10A0F}\u{10A38}-\u{10A3A}\u{10A3F}\u{10AE5}\u{10AE6}\u{10D24}-\u{10D27}\u{10D69}-\u{10D6D}\u{10EAB}\u{10EAC}\u{10EFA}-\u{10EFF}\u{10F46}-\u{10F50}\u{10F82}-\u{10F85}\u{11000}-\u{11002}\u{11038}-\u{11046}\u{11070}\u{11073}\u{11074}\u{1107F}-\u{11082}\u{110B0}-\u{110BA}\u{110BD}\u{110C2}\u{110CD}\u{11100}-\u{11102}\u{11127}-\u{11134}\u{11145}\u{11146}\u{11173}\u{11180}-\u{11182}\u{111B3}-\u{111C0}\u{111C9}-\u{111CC}\u{111CE}\u{111CF}\u{1122C}-\u{11237}\u{1123E}\u{11241}\u{112DF}-\u{112EA}\u{11300}-\u{11303}\u{1133B}\u{1133C}\u{1133E}-\u{11344}\u{11347}\u{11348}\u{1134B}-\u{1134D}\u{11357}\u{11362}\u{11363}\u{11366}-\u{1136C}\u{11370}-\u{11374}\u{113B8}-\u{113C0}\u{113C2}\u{113C5}\u{113C7}-\u{113CA}\u{113CC}-\u{113D0}\u{113D2}\u{113E1}\u{113E2}\u{11435}-\u{11446}\u{1145E}\u{114B0}-\u{114C3}\u{115AF}-\u{115B5}\u{115B8}-\u{115C0}\u{115DC}\u{115DD}\u{11630}-\u{11640}\u{116AB}-\u{116B7}\u{1171D}-\u{1172B}\u{1182C}-\u{1183A}\u{11930}-\u{11935}\u{11937}\u{11938}\u{1193B}-\u{1193E}\u{11940}\u{11942}\u{11943}\u{119D1}-\u{119D7}\u{119DA}-\u{119E0}\u{119E4}\u{11A01}-\u{11A0A}\u{11A33}-\u{11A39}\u{11A3B}-\u{11A3E}\u{11A47}\u{11A51}-\u{11A5B}\u{11A8A}-\u{11A99}\u{11B60}-\u{11B67}\u{11C2F}-\u{11C36}\u{11C38}-\u{11C3F}\u{11C92}-\u{11CA7}\u{11CA9}-\u{11CB6}\u{11D31}-\u{11D36}\u{11D3A}\u{11D3C}\u{11D3D}\u{11D3F}-\u{11D45}\u{11D47}\u{11D8A}-\u{11D8E}\u{11D90}\u{11D91}\u{11D93}-\u{11D97}\u{11EF3}-\u{11EF6}\u{11F00}\u{11F01}\u{11F03}\u{11F34}-\u{11F3A}\u{11F3E}-\u{11F42}\u{11F5A}\u{13430}-\u{13440}\u{13447}-\u{13455}\u{1611E}-\u{1612F}\u{16AF0}-\u{16AF4}\u{16B30}-\u{16B36}\u{16F4F}\u{16F51}-\u{16F87}\u{16F8F}-\u{16F92}\u{16FE4}\u{16FF0}\u{16FF1}\u{1BC9D}\u{1BC9E}\u{1BCA0}-\u{1BCA3}\u{1CF00}-\u{1CF2D}\u{1CF30}-\u{1CF46}\u{1D165}-\u{1D169}\u{1D16D}-\u{1D182}\u{1D185}-\u{1D18B}\u{1D1AA}-\u{1D1AD}\u{1D242}-\u{1D244}\u{1DA00}-\u{1DA36}\u{1DA3B}-\u{1DA6C}\u{1DA75}\u{1DA84}\u{1DA9B}-\u{1DA9F}\u{1DAA1}-\u{1DAAF}\u{1E000}-\u{1E006}\u{1E008}-\u{1E018}\u{1E01B}-\u{1E021}\u{1E023}\u{1E024}\u{1E026}-\u{1E02A}\u{1E08F}\u{1E130}-\u{1E136}\u{1E2AE}\u{1E2EC}-\u{1E2EF}\u{1E4EC}-\u{1E4EF}\u{1E5EE}\u{1E5EF}\u{1E6E3}\u{1E6E6}\u{1E6EE}\u{1E6EF}\u{1E6F5}\u{1E8D0}-\u{1E8D6}\u{1E944}-\u{1E94A}\u{E0000}-\u{E0FFF}]/u;

/**
 * An emptiness check, not a name normalizer. Marks and joining characters
 * inside a real name stay untouched; on their own they cannot name someone.
 * Keep the server display-helper mirror in step with this predicate.
 */
export function hasReadableNameBase(value: unknown): value is string {
  return typeof value === 'string' && NAME_BASE_CHARACTER.test(value);
}

function readableFirstName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) return null;
  const withoutHonorific = trimmed.replace(LEADING_HONORIFIC, '').trim();
  const first = withoutHonorific.split(/\s+/)[0] ?? '';
  // A bare title is not a first name. Left unguarded, teacherDisplayName
  // takes this as the "real" first name and re-prefixes the SAME title in
  // front of it - a teacher whose displayName is literally "Ms." rendered
  // as "Ms. Ms.", the account-level twin of the "Ms., Ms." group-title bug
  // (see defaultGroupTitle in pages/newMessage/recipients.ts).
  if (!hasReadableNameBase(first) || BARE_HONORIFIC.test(first)) return null;
  if (first === first.toLowerCase() || first === first.toUpperCase()) {
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  return first;
}

export function teacherFirstName(
  user: Pick<TeacherIdentity, 'displayName'> | null | undefined
): string | null {
  return readableFirstName(user?.displayName);
}

/**
 * The same "typed it in all lowercase (or all caps) at signup and nobody has
 * touched it since" fix readableFirstName already applies to a first name,
 * extended to every word so the untitled fallback label below does not read
 * oddly next to every titled name around it (e.g. "aadison kampert" with no
 * teacherTitle chosen yet). Mixed case is left exactly alone: that is a real
 * stylistic choice (McKinney, O'Brien), never a typo to correct. An
 * email-shaped string is left alone too; this only styles a NAME.
 */
function normalizeFallbackNameCase(value: string): string {
  if (value.includes('@')) return value;
  const isAllLower = value === value.toLowerCase();
  const isAllUpper = value === value.toUpperCase();
  if (!isAllLower && !isAllUpper) return value;
  return value
    .split(/(\s+)/)
    .map((part) => (/^\s+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
    .join('');
}

/**
 * THE ONE PLACEHOLDER FOR A PERSON WE CANNOT NAME.
 *
 * Every name-rendering fallback in the Hub ends here, and none of them may
 * end at an identifier. Bobbie, 2026-08-28, about a Jr assistant: "It's
 * showing up as a weird number for him not his actual name." That weird
 * number was a uid slice. A dancer sign-in's uid is a randomUUID
 * (netlify/functions/dancerLogin.mjs createDancerLogin), so its first six
 * characters are hex and read to a human as a number.
 *
 * The rule is CLAUDE.md's golden rule applied to names: a missing name shows
 * a neutral placeholder so the gap stays VISIBLE, never an email address and
 * never an identifier. A blank spot gets reported and filled in. "Jr Staff
 * 4f1a2b" gets read as the person's name and believed.
 */
export const UNNAMED_PERSON_LABEL = 'Someone';

export function teacherTitleLabel(title: TeacherTitle): 'Ms.' | 'Coach' {
  return title === 'coach' ? 'Coach' : 'Ms.';
}

/** Every active teaching role chooses a title, including staff who also dance. */
export function teacherCanChooseTitle(user: TeacherTitleAccount | null | undefined): boolean {
  return user?.status === 'active' && ['director', 'manager', 'company', 'ensemble'].includes(user.role ?? '');
}

/**
 * Who a DIRECTOR may assign a title to, which is a wider set than who may
 * choose their own.
 *
 * WHY THE TWO DIFFER, and why this is not teacherCanChooseTitle with an
 * extra role bolted on. teacherCanChooseTitle answers "may the person in
 * front of me set their OWN title", so it is about the signed-in account and
 * it needs a live Firebase session by definition. This answers "may a
 * Director set THIS person's title", and the whole reason the picker exists
 * is the people who can never answer the first question: a Jr Staff whose
 * only sign-in is a dancer login has no Firebase Auth principal at all, so
 * setTeacherTitle's self path is unreachable for them forever. Bobbie,
 * 2026-08-28, about one of them: "It's showing up as a weird number for him
 * not his actual name."
 *
 * Three populations qualify, matching the server's directorMayTitle():
 *   - any ACTIVE staff account, Admin (foh) included, per her ruling that
 *     the first-name rule covers "teachers, Jr Staff, Admin and Directors
 *     alike";
 *   - a dancer login PROMOTED to Jr Staff, which is already role 'ensemble'
 *     status 'active' and so needs no special case here;
 *   - an UNPROMOTED dancer login (role 'family', status 'dancer') whose
 *     dancer is really marked Jr Staff. The caller supplies that last fact,
 *     because it lives in the designation ledger and not on the account.
 *
 * THIS IS PRESENTATION ONLY. The real boundary is the server's own read of
 * the caller's and the target's docs (netlify/functions/setTeacherTitle.mjs),
 * plus the rules that freeze teacherTitle against every client write. A
 * client `if` is not a permission; this one only decides what to draw.
 */
export function directorCanAssignTitle(
  user: TeacherTitleAccount | null | undefined,
  options?: { jrStaffDesignated?: boolean }
): boolean {
  if (!user) return false;
  const role = user.role ?? '';
  if (user.status === 'active' && ['director', 'manager', 'company', 'ensemble', 'foh'].includes(role)) {
    return true;
  }
  return role === 'family' && user.status === 'dancer' && options?.jrStaffDesignated === true;
}

/**
 * The one teacher label used by every linked-account surface.
 *
 * Once a teacher chooses a title, the result is intentionally exact:
 * "Ms. Firstname" or "Coach Firstname". Before they choose, the existing
 * account name remains visible so the rollout never turns a known teacher
 * into an email address or an opaque uid.
 */
export function teacherDisplayName(
  user: TeacherIdentity
): string {
  const firstName = teacherFirstName(user);
  if (user.teacherTitle && firstName) {
    return `${teacherTitleLabel(user.teacherTitle)} ${firstName}`;
  }
  const rawName = typeof user.displayName === 'string' ? user.displayName.trim() : '';
  // A title followed only by invisible characters is not a usable name.
  // A legacy bare title still keeps its existing once-only fallback policy.
  if (hasReadableNameBase(rawName.replace(LEADING_HONORIFIC, ''))) return normalizeFallbackNameCase(rawName);
  /**
   * NEVER an email address. Reported through App Feedback, 2026-08-20: "It
   * says my name at login, but it's showing my email address in the classes."
   * This helper feeds class rosters, class posts, the calendar and the "who
   * assists it" row, so an account with no displayName was publishing its
   * owner's email to everyone who could see that class. A display name is a
   * name; an email address is contact information, and it is not ours to
   * broadcast because a profile field is blank.
   *
   * The uid slice is gone too - it was unreadable and told nobody anything.
   * A neutral placeholder leaves the gap VISIBLE so the studio fills the name
   * in, which is the golden rule: a missing sentence gets noticed, an invented
   * one gets believed. Admin screens are unaffected; ManageUsers never calls
   * this and shows the email in its own field.
   */
  return UNNAMED_PERSON_LABEL;
}

/**
 * The name a screen may show when the only thing it holds is a DENORMALIZED
 * snapshot: offerings.assistantNames or offerings.teacherNames, written at
 * save time precisely because most viewers can never resolve the account
 * behind the uid (see OfferingDoc.assistantNames: a designated dancer's
 * login is a family-role doc that the users rules deny to an ensemble viewer
 * and that every non-Director users query filters out by status).
 *
 * A stored value that is empty, or that is an address rather than a name,
 * falls through to the placeholder for the same reason teacherDisplayName
 * refuses one. The snapshot builders cannot emit an address today; a
 * document written before they existed is still not ours to broadcast.
 *
 * THE SURNAME DECISION, ON THE RECORD. Every class surface in the Hub now
 * funnels through this one helper, so what it does with a FULL stored name
 * is a policy rather than an implementation detail, and it is settled here
 * instead of being left to whatever each snapshot happens to hold.
 *
 * It returns the FIRST NAME. Bobbie, 2026-08-28: "on everything teachers
 * only want first name not last for the website and in the app. So it is Ms
 * Dallas not Dallas Brown anywhere." A snapshot written for an account that
 * had already chosen a title is already "Ms. Firstname" or "Coach
 * Firstname" and survives verbatim, honorific kept. A snapshot written for
 * an UNTITLED account is that person's raw display name, surname and all,
 * and publishing "Parker Gavina" to a family is exactly the leak the ruling
 * names. So an untitled snapshot publishes the first name alone with NO
 * honorific: Ms. and Coach are not interchangeable and guessing one is not
 * ours to do, same ruling, same paragraph.
 *
 * This is the identical posture publicTeacherLabel() already ships on the
 * public marketing site (netlify/functions/lib/teacherTitle.mjs), which is
 * the point: the website and the app say the same thing about one person.
 *
 * NOT A REVERSAL OF THE STAFF-ONLY RULING. Payroll, the Audit Log and
 * PrintRosters keep full names (Bobbie, 2026-08-28: "Keep full names on all
 * three") and none of the three calls this helper, so reducing here cannot
 * reach them. Search is untouched too: teacherSearchText and
 * teacherMatchNames still carry the raw name, per her 2026-08-14
 * findability ruling.
 */
export function teacherNameFromSnapshot(storedName: string | null | undefined): string {
  const stored = typeof storedName === 'string' ? storedName.trim() : '';
  if (!stored || stored.includes('@')) return UNNAMED_PERSON_LABEL;
  const firstName = readableFirstName(stored);
  if (!firstName) return UNNAMED_PERSON_LABEL;
  // Only the app's own two-word title vocabulary is carried through. Any
  // other honorific in a legacy snapshot is dropped rather than translated:
  // readableFirstName already strips it, and turning a "Mr." into a "Ms."
  // would be inventing a title for somebody, which is the one thing the
  // ruling forbids.
  if (/^Ms\.?\s+\S/i.test(stored)) return `${teacherTitleLabel('ms')} ${firstName}`;
  if (/^Coach\s+\S/i.test(stored)) return `${teacherTitleLabel('coach')} ${firstName}`;
  return firstName;
}

/**
 * R45: only the last stored token's initial from the readable snapshot.
 * A snapshot cannot prove that token is a surname rather than part of a
 * compound given name, so callers must not describe the extraction as more.
 */
export function teacherSurnameInitialFromSnapshot(storedName: string): string {
  if (!storedName || storedName.includes('@')) return '';
  const parts = storedName.trim().replace(LEADING_HONORIFIC, '').trim().split(/\s+/);
  if (parts.length < 2) return '';
  const first = Array.from(parts[parts.length - 1] ?? '')[0] ?? '';
  // Case mapping covers cased scripts without syntax that older browsers
  // cannot parse. An unusable or uncased initial keeps the first-name fallback.
  return first.toUpperCase() !== first.toLowerCase() ? `${Array.from(first.toUpperCase())[0]}.` : '';
}

/**
 * The whole chain a class surface should use to name one staff uid: the live
 * account when this viewer can read it, then the offering's own snapshot,
 * then the honest placeholder. Never the uid.
 */
export function teacherLabelOrPlaceholder(
  user: TeacherIdentity | null | undefined,
  storedName?: string | null
): string {
  if (user) return teacherDisplayName(user);
  return teacherNameFromSnapshot(storedName);
}

/**
 * THE SECOND LEGITIMATE EXEMPTION, and the only other one in the app.
 *
 * A STAFF-ONLY OPERATIONAL ROW that must tell two unnamed people apart may
 * append a short handle to the placeholder, and this is the one function
 * allowed to do it, so the exemption is written down once instead of being
 * re-argued at four call sites.
 *
 * WHY IT HAS TO EXIST. Collapsing every unnameable person to a bare
 * "Someone" fixed the leak and introduced a worse operational bug: the
 * Attendance card is where a teacher marks Jr assistants present, and two
 * assistants on one class rendered as TWO ROWS BOTH LABELLED "Someone",
 * each with its own present/absent toggle writing to a different uid. A
 * teacher could mark the wrong person with nothing on screen to tell them
 * apart. Same shape on the Jr Staff reliability report, and on the Command
 * Center and sub board rows that read "Covered by Someone" and "Asked by
 * Someone" twice over.
 *
 * WHY IT IS NOT THE BUG IT LOOKS LIKE. Bobbie's report was an identifier
 * standing in FOR a name ("a weird number for him not his actual name"). A
 * handle here never stands in for a name: the placeholder is right there
 * saying we do not know who this is, and the handle only distinguishes one
 * unknown from another. It is the same reasoning as the class builder's
 * stale chips, which say "No longer Jr Staff" and carry an id purely so a
 * Director can remove the right one.
 *
 * THE BOUNDARY. Staff-only operational screens only. Never a family or
 * dancer surface, and never a row that already has a name: the handle is
 * appended ONLY when the whole chain has already fallen through to the
 * placeholder. Where a screen just needs to name somebody, use
 * teacherLabelOrPlaceholder and let the gap show.
 */
export function teacherLabelForStaffRow(
  user: TeacherIdentity | null | undefined,
  storedName: string | null | undefined,
  uid: string | null | undefined
): string {
  const label = teacherLabelOrPlaceholder(user, storedName);
  if (label !== UNNAMED_PERSON_LABEL) return label;
  // id-ok: a HANDLE, never a name. Reached only once the label is already
  // the placeholder, so nothing here is standing in for somebody's name;
  // it exists so two unnamed rows on one operational screen cannot be
  // confused for each other. See this function's own note.
  const handle = typeof uid === 'string' ? uid.slice(0, 6) : ''; // id-ok: see the note above
  return handle ? `${UNNAMED_PERSON_LABEL} (${handle})` : UNNAMED_PERSON_LABEL;
}

/** Search the exact rendered title as well as the underlying account fields. */
export function teacherSearchText(user: TeacherIdentity): string {
  // email-ok: SEARCH text, never rendered. A director typing a parent's
  // address into a search box still has to find them.
  return [teacherDisplayName(user), user.displayName, user.email]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase();
}

/**
 * Discrete name atoms for FUZZY search tolerance, kept apart from each other
 * and from teacherSearchText's glued blob on purpose.
 *
 * fuzzyMatchScore's edit-distance is length-normalized, so a misspelling of
 * a short first name loses badly once it is compared against a long string:
 * "clarie" against "Ms. Claire" alone scores .5, under the .6 bar every
 * caller in the app uses, but the same misspelling against bare "Claire"
 * scores .67 and clears it easily. Gluing more text on (an honorific, a
 * last name, an email) only makes this worse, never better.
 *
 * Bobbie, 2026-08-14 evening (recorded in DECISIONS.md): a person has to be
 * findable by the name they are actually known by, not only the styled
 * "Ms. Firstname" the app renders. That real name is exactly what this
 * returns and teacherSearchText's blob does not carry cleanly for fuzzy
 * matching: the account's own first name, and the account's full raw
 * display name (so a full "First Last" typed in either order still scores
 * through fuzzyMatchScore's word-overlap path). Callers fuzzy-match the
 * query against each of these PLUS the styled label separately and keep the
 * best score, they never fuzzy-match against one concatenated string.
 */
export function teacherMatchNames(user: TeacherIdentity): string[] {
  const rawName = hasReadableNameBase(user.displayName) ? user.displayName.trim() : null;
  const names = [teacherFirstName(user), rawName];
  return names.filter((name): name is string => typeof name === 'string' && name.length > 0);
}

/** Never returns an email or uid for a family-facing denormalized name. */
export function teacherNameForFamilyCopy(
  user: Pick<TeacherIdentity, 'displayName' | 'teacherTitle'>
): string | null {
  const firstName = teacherFirstName(user);
  if (!firstName) return null;
  return user.teacherTitle
    ? `${teacherTitleLabel(user.teacherTitle)} ${firstName}`
    : user.displayName?.trim() || firstName;
}

/** Whether a reduced snapshot already ends in one usable surname initial. */
export function hasReducedSurnameInitial(value: string): boolean {
  const last = value.trim().split(/\s+/).at(-1) ?? '';
  const points = Array.from(last);
  return points.length === 2 && points[1] === '.' && points[0].toUpperCase() !== points[0].toLowerCase();
}
